import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { DashboardSettingsModal } from "./DashboardSettingsModal";
import { DashboardEditorPanel } from "@/components/dashboard/DashboardEditorPanel";
import { useSectionDashboard } from "@/hooks/useSectionDashboard";

export default function DashboardEditorPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const {
    config,
    isLoading,
    sortedSections,
    save,
    updateSettings,
    addSection,
    updateSection,
    deleteSection,
    moveSectionUp,
    moveSectionDown,
    updateSectionPlacements,
    addWidgetToSection,
    renameWidget,
    duplicateWidget,
    removeWidget,
    moveWidgetToSection,
  } = useSectionDashboard(slug ?? "");

  const [settingsOpen, setSettingsOpen] = useState(false);

  if (!config) {
    return (
      <AdminLayout
        pageTitle="Dashboard Editor"
        breadcrumbs={[
          { title: "Management", href: "/admin" },
          { title: "Dashboards", href: "/admin/dashboards" },
        ]}
      >
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          {isLoading ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : (
            <>
              <p className="text-sm text-muted">Dashboard not found.</p>
              <Button variant="outline" onClick={() => navigate("/admin/dashboards")}>
                Back to Dashboards
              </Button>
            </>
          )}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      pageTitle={config.name}
      breadcrumbs={[
        { title: "Management", href: "/admin" },
        { title: "Dashboards", href: "/admin/dashboards" },
      ]}
    >
      <DashboardSettingsModal
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        name={config.name}
        description={config.description}
        slug={config.slug}
        onSave={updateSettings}
      />
      <DashboardEditorPanel
        config={config}
        sortedSections={sortedSections}
        onSave={() => save(config)}
        addSection={addSection}
        updateSection={updateSection}
        deleteSection={deleteSection}
        moveSectionUp={moveSectionUp}
        moveSectionDown={moveSectionDown}
        updateSectionPlacements={updateSectionPlacements}
        addWidgetToSection={addWidgetToSection}
        renameWidget={renameWidget}
        duplicateWidget={duplicateWidget}
        removeWidget={removeWidget}
        moveWidgetToSection={moveWidgetToSection}
        onSettings={() => setSettingsOpen(true)}
        onPreview={() => navigate(`/admin/dashboards/${config.slug}`)}
      />
    </AdminLayout>
  );
}
